import type { TFunction } from "i18next";
import type { GridColDef } from "@act-group/act-ui-library";
import { Verification } from "../../types/verification";
import { DeclineReasonsList } from "../DeclineReasonsList";
import { ChecksList } from "../ChecksList";
import { formatStatusLabel } from "../../utils/verificationLabels";

// Hidden for now. Kept as a flag rather than deleted so the column comes back
// with a one-line flip — the API still sends `profile` on every row.
const SHOW_PROFILE_COLUMN: boolean = false;

// Built via a factory (not a module constant) so the headers re-translate when
// the language changes — the caller passes its `t` from useTranslation.
export function getVerificationColumns(t: TFunction): GridColDef<Verification>[] {
  return [
    {
      field: "fullName",
      headerName: t("Name"),
      flex: 1,
      minWidth: 180,
      valueGetter: (_value, row) => row.fullName || "—",
    },
    ...(SHOW_PROFILE_COLUMN ? [{ field: "profile", headerName: t("Profile"), width: 150 }] : []),
    {
      field: "status",
      headerName: t("Status"),
      width: 130,
      // Same Title-Case + translated label the detail page's status chips use,
      // so a status reads identically in the grid and on the detail page.
      valueGetter: (_value, row) => t(formatStatusLabel(row.status)),
    },
    {
      field: "checks",
      headerName: t("Checks"),
      flex: 1,
      // Wide enough for four chips plus "show more..." before the cell clips.
      minWidth: 480,
      sortable: false,
      renderCell: (params) => <ChecksList checks={params.row.checks} />,
    },
    {
      field: "declinedReasons",
      headerName: t("Decline Reasons"),
      flex: 1,
      minWidth: 220,
      sortable: false,
      renderCell: (params) => <DeclineReasonsList reasons={params.row.declinedReasons} />,
    },
    // "External ID" is mapped to the API's verificationId.
    { field: "verificationId", headerName: t("External ID"), flex: 1, minWidth: 240 },
    {
      field: "updatedAt",
      headerName: t("Updated At"),
      width: 200,
      valueGetter: (_value, row) => new Date(row.updatedAt).toLocaleString(),
    },
  ];
}
